import { basename, dirname, isAbsolute, join, relative, resolve as resolvePath, sep } from "node:path";
import { StringDecoder } from "node:string_decoder";
import {
	DEFAULT_MAX_BYTES,
	DEFAULT_MAX_LINES,
	formatSize,
	type TruncationResult,
	truncateHead,
} from "@caupulican/pi-agent-core/truncate";
import type { AgentTool } from "@caupulican/pi-agent-core/types";
import type { Api, ImageContent, Model, TextContent } from "@caupulican/pi-ai";
import { StreamingLineDecoder } from "@caupulican/pi-ai/streaming-lines";
import { Text } from "@caupulican/pi-tui";
import { constants } from "fs";
import { access as fsAccess, open as fsOpen, readFile as fsReadFile, stat as fsStat } from "fs/promises";
import { type Static, Type } from "typebox";
import { getAgentDir, getReadmePath } from "../../config.ts";
import { keyHint, keyText } from "../../modes/interactive/components/keybinding-hints.ts";
import { getLanguageFromPath, highlightCode, type Theme } from "../../modes/interactive/theme/theme.ts";
import { formatDimensionNote, resizeImage } from "../../utils/image-resize.ts";
import { detectSupportedImageMimeTypeFromFile } from "../../utils/mime.ts";
import { formatPathRelativeToCwdOrAbsolute } from "../../utils/paths.ts";
import { getProcessWorkRun } from "../../utils/work-directory.ts";
import type { ToolDefinition, ToolRenderResultOptions } from "../extensions/types.ts";
import {
	FILE_CURRENT_TEXT_RECOVERY_TARGET_KIND,
	FILE_EXISTS_RECOVERY_TARGET_KIND,
	type FileFailureRecoveryAuthority,
	fileRecoveryTarget,
	selectFileFailureRecoveryAuthority,
} from "./file-failure-recovery.ts";
import { resolveReadPathAsync, resolveToCwd } from "./path-utils.ts";
import { getTextOutput, renderToolPath, replaceTabs, str } from "./render-utils.ts";
import { isPiSessionJsonlPath, projectPiSessionJsonlLine } from "./session-transcript-read.ts";
import { wrapToolDefinition } from "./tool-definition-wrapper.ts";

const readSchema = Type.Object({
	path: Type.String({ description: "Path to the file to read (relative or absolute)" }),
	offset: Type.Optional(Type.Number({ description: "Line number to start reading from (1-indexed)" })),
	limit: Type.Optional(Type.Number({ description: "Maximum number of lines to read" })),
	lineNumbers: Type.Optional(Type.Boolean({ description: "Include line numbers in the output" })),
	tail: Type.Optional(Type.Number({ description: "Number of lines to read from the end of the file" })),
	filter: Type.Optional(
		Type.Union([Type.Literal("none"), Type.Literal("minimal"), Type.Literal("aggressive")], {
			description: "Safe text filtering level (none, minimal, aggressive)",
		}),
	),
});

export type ReadToolInput = Static<typeof readSchema>;

export interface ReadToolDetails {
	truncation?: TruncationResult;
}

interface CompactReadClassification {
	kind: "docs" | "resource" | "skill";
	label: string;
}

const COMPACT_RESOURCE_FILE_NAMES = new Set([
	"AGENTS.md",
	"AGENTS.MD",
	"CLAUDE.md",
	"CLAUDE.MD",
	"GEMINI.md",
	"GEMINI.MD",
]);

/**
 * Pluggable operations for the read tool.
 * Override these to delegate file reading to remote systems (for example SSH).
 */
export interface ReadOperations {
	/** Read file contents as a Buffer */
	readFile: (absolutePath: string) => Promise<Buffer>;
	/** Check if file is readable (throw if not) */
	access: (absolutePath: string) => Promise<void>;
	/** Detect image MIME type, return null or undefined for non-images */
	detectImageMimeType?: (absolutePath: string) => Promise<string | null | undefined>;
	/** File size in bytes, used to decide between whole-file and sliced reads. */
	stat?: (absolutePath: string) => Promise<{ size: number }>;
	/**
	 * Stream a slice of lines out of the file with bounded memory. Any region of an
	 * arbitrarily large file stays reachable in batches via offset continuation.
	 */
	readLineSlice?: (absolutePath: string, options: LineSliceOptions) => Promise<LineSlice>;
	/** Count lines by streaming (bounded memory); used to resolve tail reads on oversized files. */
	countLines?: (absolutePath: string) => Promise<number>;
}

export interface LineSliceOptions {
	/** 0-based line to start collecting at. */
	startLine: number;
	/** Maximum number of lines to collect. */
	maxLines: number;
	/** Maximum total characters to collect across lines. */
	maxChars: number;
}

export interface LineSlice {
	lines: { text: string; originalIndex: number }[];
	/** True when the end of the file was reached while collecting. */
	reachedEnd: boolean;
}

const SLICE_SCAN_CHUNK_BYTES = 1024 * 1024;

async function scanLines(
	absolutePath: string,
	onLine: (text: string | undefined, index: number) => boolean,
): Promise<void> {
	const handle = await fsOpen(absolutePath, "r");
	try {
		const byteDecoder = new StringDecoder("utf8");
		const lineDecoder = new StreamingLineDecoder(Number.MAX_SAFE_INTEGER, { lineEndings: "lf" });
		const buffer = Buffer.allocUnsafe(SLICE_SCAN_CHUNK_BYTES);
		let index = 0;
		let emittedAnyLine = false;
		while (true) {
			const { bytesRead } = await handle.read(buffer, 0, buffer.length, null);
			if (bytesRead === 0) break;
			const lines = lineDecoder.push(byteDecoder.write(buffer.subarray(0, bytesRead)));
			for (const line of lines) {
				emittedAnyLine = true;
				if (!onLine(line, index++)) return;
			}
		}
		for (const line of lineDecoder.push(byteDecoder.end())) {
			emittedAnyLine = true;
			if (!onLine(line, index++)) return;
		}
		const finalLine = lineDecoder.finish();
		if (finalLine !== undefined || !emittedAnyLine) {
			onLine(finalLine ?? "", index);
		}
	} finally {
		await handle.close();
	}
}

async function readLocalLineSlice(absolutePath: string, options: LineSliceOptions): Promise<LineSlice> {
	const lines: { text: string; originalIndex: number }[] = [];
	let collectedChars = 0;
	let sawMore = false;
	await scanLines(absolutePath, (text, index) => {
		if (text === undefined) return false;
		if (index < options.startLine) return true;
		if (lines.length >= options.maxLines || collectedChars > options.maxChars) {
			sawMore = true;
			return false;
		}
		lines.push({ text, originalIndex: index + 1 });
		collectedChars += text.length;
		return true;
	});
	return { lines, reachedEnd: !sawMore };
}

async function countLocalLines(absolutePath: string): Promise<number> {
	let count = 0;
	await scanLines(absolutePath, () => {
		count++;
		return true;
	});
	return count;
}

const defaultReadOperations: ReadOperations = {
	readFile: (path) => fsReadFile(path),
	access: (path) => fsAccess(path, constants.R_OK),
	detectImageMimeType: detectSupportedImageMimeTypeFromFile,
	stat: async (path) => ({ size: (await fsStat(path)).size }),
	readLineSlice: readLocalLineSlice,
	countLines: countLocalLines,
};

// Loading a whole file before truncation lets a single giant file spike the heap
// to its full size. Beyond these budgets, text reads stream line slices (every
// region stays reachable in batches — lossless). The image budget is deliberately
// far above any real screenshot/photo so quality-degrading workarounds are never
// needed in practice; it only guards against pathological files.
const DEFAULT_MAX_TEXT_READ_BYTES = 16 * 1024 * 1024;
const DEFAULT_MAX_IMAGE_READ_BYTES = 128 * 1024 * 1024;

export interface ReadToolOptions {
	/** Whether to auto-resize images to 2000x2000 max. Default: true */
	autoResizeImages?: boolean;
	/** Custom operations for file reading. Default: local filesystem */
	operations?: ReadOperations;
	/** Shared backend identity for exact cross-tool recovery with custom operations. */
	failureRecoveryAuthority?: FileFailureRecoveryAuthority;
	/** Whole-file load budget for text reads; larger files stream line slices instead. Default 16 MiB. */
	maxTextReadBytes?: number;
	/** Pathology guard for image reads; larger images return downscale guidance. Default 128 MiB. */
	maxImageReadBytes?: number;
}

type ReadRenderArgs = { path?: string; file_path?: string; offset?: number; limit?: number };

function formatReadLineRange(args: ReadRenderArgs | undefined, theme: Theme): string {
	if (args?.offset === undefined && args?.limit === undefined) return "";
	const startLine = args.offset ?? 1;
	const endLine = args.limit !== undefined ? startLine + args.limit - 1 : "";
	return theme.fg("warning", `:${startLine}${endLine ? `-${endLine}` : ""}`);
}

function formatReadCall(args: ReadRenderArgs | undefined, theme: Theme, cwd: string): string {
	const pathDisplay = renderToolPath(str(args?.file_path ?? args?.path), theme, cwd);
	return `${theme.fg("toolTitle", theme.bold("read"))} ${pathDisplay}${formatReadLineRange(args, theme)}`;
}

function trimTrailingEmptyLines(lines: string[]): string[] {
	let end = lines.length;
	while (end > 0 && lines[end - 1] === "") {
		end--;
	}
	return lines.slice(0, end);
}

function splitContentLines(text: string): string[] {
	const lines = text.split("\n");
	if (text.endsWith("\n")) lines.pop();
	return lines;
}

function getNonVisionImageNote(model: Model<Api> | undefined): string | undefined {
	if (!model || model.input.includes("image")) {
		return undefined;
	}
	return "[Current model does not support images. The image will be omitted from this request.]";
}

function toPosixPath(filePath: string): string {
	return filePath.split(sep).join("/");
}

function getPiDocsClassification(absolutePath: string): CompactReadClassification | undefined {
	const packageRoot = dirname(getReadmePath());
	const relativePath = relative(resolvePath(packageRoot), resolvePath(absolutePath));
	if (
		relativePath === "" ||
		relativePath === ".." ||
		relativePath.startsWith(`..${sep}`) ||
		isAbsolute(relativePath)
	) {
		return undefined;
	}

	const label = toPosixPath(relativePath);
	if (label === "README.md" || label.startsWith("docs/") || label.startsWith("examples/")) {
		return { kind: "docs", label };
	}
	return undefined;
}

function getCompactReadClassification(
	args: ReadRenderArgs | undefined,
	cwd: string,
): CompactReadClassification | undefined {
	const rawPath = str(args?.file_path ?? args?.path);
	if (!rawPath) return undefined;

	const absolutePath = resolveToCwd(rawPath, cwd);
	const fileName = basename(absolutePath);
	if (fileName === "SKILL.md") {
		return { kind: "skill", label: basename(dirname(absolutePath)) || fileName };
	}

	const docsClassification = getPiDocsClassification(absolutePath);
	if (docsClassification) return docsClassification;

	if (COMPACT_RESOURCE_FILE_NAMES.has(fileName)) {
		return { kind: "resource", label: formatPathRelativeToCwdOrAbsolute(absolutePath, cwd) };
	}

	return undefined;
}

function formatCompactReadCall(
	classification: CompactReadClassification,
	args: ReadRenderArgs | undefined,
	theme: Theme,
): string {
	const expandHint = theme.fg("dim", ` (${keyText("app.tools.expand")} to expand)`);
	if (classification.kind === "skill") {
		return (
			theme.fg("customMessageLabel", `\x1b[1m[skill]\x1b[22m `) +
			theme.fg("customMessageText", classification.label) +
			formatReadLineRange(args, theme) +
			expandHint
		);
	}

	return (
		theme.fg("toolTitle", theme.bold(`read ${classification.kind}`)) +
		" " +
		theme.fg("accent", classification.label) +
		formatReadLineRange(args, theme) +
		expandHint
	);
}

function formatReadResult(
	args: ReadRenderArgs | undefined,
	result: { content: (TextContent | ImageContent)[]; details?: ReadToolDetails },
	options: ToolRenderResultOptions,
	theme: Theme,
	showImages: boolean,
	_cwd: string,
	isError: boolean,
): string {
	if (!options.expanded && !isError) {
		return "";
	}

	const rawPath = str(args?.file_path ?? args?.path);
	const output = getTextOutput(result, showImages);
	const lang = !isError && rawPath ? getLanguageFromPath(rawPath) : undefined;
	const renderedLines = lang ? highlightCode(replaceTabs(output), lang) : output.split("\n");
	const lines = trimTrailingEmptyLines(renderedLines);
	const maxLines = options.expanded ? lines.length : 10;
	const displayLines = lines.slice(0, maxLines);
	const remaining = lines.length - maxLines;
	let text = `\n${displayLines.map((line) => (lang ? replaceTabs(line) : theme.fg("toolOutput", replaceTabs(line)))).join("\n")}`;
	if (remaining > 0) {
		text += `${theme.fg("muted", `\n... (${remaining} more lines,`)} ${keyHint("app.tools.expand", "to expand")})`;
	}

	const truncation = result.details?.truncation;
	if (truncation?.truncated) {
		if (truncation.firstLineExceedsLimit) {
			text += `\n${theme.fg("warning", `[First line exceeds ${formatSize(truncation.maxBytes ?? DEFAULT_MAX_BYTES)} limit]`)}`;
		} else if (truncation.truncatedBy === "lines") {
			text += `\n${theme.fg("warning", `[Truncated: showing ${truncation.outputLines} of ${truncation.totalLines} lines (${truncation.maxLines ?? DEFAULT_MAX_LINES} line limit)]`)}`;
		} else {
			text += `\n${theme.fg("warning", `[Truncated: ${truncation.outputLines} lines shown (${formatSize(truncation.maxBytes ?? DEFAULT_MAX_BYTES)} limit)]`)}`;
		}
	}
	return text;
}

export function createReadToolDefinition(
	cwd: string,
	options?: ReadToolOptions,
): ToolDefinition<typeof readSchema, ReadToolDetails | undefined> {
	const autoResizeImages = options?.autoResizeImages ?? true;
	const ops = options?.operations ?? defaultReadOperations;
	const failureRecoveryAuthority = selectFileFailureRecoveryAuthority(
		options?.operations !== undefined,
		options?.failureRecoveryAuthority,
	);
	const maxTextReadBytes = options?.maxTextReadBytes ?? DEFAULT_MAX_TEXT_READ_BYTES;
	const maxImageReadBytes = options?.maxImageReadBytes ?? DEFAULT_MAX_IMAGE_READ_BYTES;
	const configuredSessionDirectory = join(getAgentDir(), "sessions");
	return {
		name: "read",
		label: "read",
		description: `Read the contents of a file. Supports text files and images (jpg, png, gif, webp). Images are sent as attachments. For text files, output is truncated to ${DEFAULT_MAX_LINES} lines or ${DEFAULT_MAX_BYTES / 1024}KB (whichever is hit first). Use offset/limit for large files. When you need the full file, continue with offset until complete.`,
		promptSnippet: "Read file contents",
		promptGuidelines: ["Use read to examine files instead of cat or sed."],
		parameters: readSchema,
		failureRecovery: {
			getFailureTargets: (params, failure) =>
				failure.failureCode === "file_not_found" && failureRecoveryAuthority
					? [fileRecoveryTarget(failureRecoveryAuthority, FILE_EXISTS_RECOVERY_TARGET_KIND, params.path, cwd)]
					: [],
			actions: failureRecoveryAuthority
				? [
						{
							kind: "correct",
							authority: failureRecoveryAuthority.contractAuthority,
							targetKind: FILE_CURRENT_TEXT_RECOVERY_TARGET_KIND,
							instruction:
								"Use read on the failed edit path to obtain current text, then submit a changed edit with exact current text.",
						},
					]
				: [],
		},
		async execute(
			_toolCallId,
			{
				path,
				offset,
				limit,
				lineNumbers,
				tail,
				filter,
			}: {
				path: string;
				offset?: number;
				limit?: number;
				lineNumbers?: boolean;
				tail?: number;
				filter?: "none" | "minimal" | "aggressive";
			},
			signal?: AbortSignal,
			_onUpdate?,
			ctx?,
		) {
			return new Promise<{ content: (TextContent | ImageContent)[]; details: ReadToolDetails | undefined }>(
				(resolve, reject) => {
					if (signal?.aborted) {
						reject(new Error("Operation aborted"));
						return;
					}
					let aborted = false;
					const onAbort = () => {
						aborted = true;
						reject(new Error("Operation aborted"));
					};
					signal?.addEventListener("abort", onAbort, { once: true });

					(async () => {
						try {
							const absolutePath = await resolveReadPathAsync(path, cwd);
							if (aborted) return;
							// Check if file exists and is readable.
							await ops.access(absolutePath);
							if (aborted) return;
							const mimeType = ops.detectImageMimeType ? await ops.detectImageMimeType(absolutePath) : undefined;
							let content: (TextContent | ImageContent)[];
							let details: ReadToolDetails | undefined;
							const nonVisionImageNote = getNonVisionImageNote(ctx?.model);
							const fileSize = ops.stat ? (await ops.stat(absolutePath)).size : undefined;
							if (aborted) return;
							if (mimeType) {
								if (fileSize !== undefined && fileSize > maxImageReadBytes) {
									signal?.removeEventListener("abort", onAbort);
									resolve({
										content: [
											{
												type: "text",
												text: `Image file is ${formatSize(fileSize)} (${formatSize(maxImageReadBytes)} inline decode limit). Downscale it first, e.g. with bash (ImageMagick: magick "${path}" -resize 2000x2000 "${join(getProcessWorkRun(getAgentDir(), "images", "previews").path, "preview.png")}") and read the result.`,
											},
										],
										details: undefined,
									});
									return;
								}
								// Read image as binary.
								const buffer = await ops.readFile(absolutePath);
								if (autoResizeImages) {
									// Resize image if needed before sending it back to the model.
									const resized = await resizeImage(buffer, mimeType);
									if (!resized) {
										let textNote = `Read image file [${mimeType}]\n[Image omitted: could not be resized below the inline image size limit.]`;
										if (nonVisionImageNote) textNote += `\n${nonVisionImageNote}`;
										content = [{ type: "text", text: textNote }];
									} else {
										const dimensionNote = formatDimensionNote(resized);
										let textNote = `Read image file [${resized.mimeType}]`;
										if (dimensionNote) textNote += `\n${dimensionNote}`;
										if (nonVisionImageNote) textNote += `\n${nonVisionImageNote}`;
										content = [
											{ type: "text", text: textNote },
											{ type: "image", data: resized.data, mimeType: resized.mimeType },
										];
									}
								} else {
									let textNote = `Read image file [${mimeType}]`;
									if (nonVisionImageNote) textNote += `\n${nonVisionImageNote}`;
									content = [
										{ type: "text", text: textNote },
										{ type: "image", data: buffer.toString("base64"), mimeType },
									];
								}
							} else {
								// Read text content. Oversized files are streamed as line slices so
								// any region stays reachable in batches without loading the whole file.
								const useSlicedRead =
									fileSize !== undefined && fileSize > maxTextReadBytes && ops.readLineSlice !== undefined;
								let startLine = 0;
								let userLimitedLines = limit;
								let totalFileLines: number | undefined;
								let moreContentRemains = false;
								let textContentForJsonCheck: string | undefined;
								let slicedLines: { text: string; originalIndex: number }[];
								if (useSlicedRead && ops.readLineSlice) {
									if (offset !== undefined) {
										startLine = Math.max(0, offset - 1);
									} else if (tail !== undefined) {
										const counted = ops.countLines ? await ops.countLines(absolutePath) : undefined;
										if (counted !== undefined) {
											totalFileLines = counted;
											startLine = Math.max(0, counted - tail);
										}
										if (limit === undefined) {
											userLimitedLines = tail;
										}
									}
									const slice = await ops.readLineSlice(absolutePath, {
										startLine,
										maxLines: userLimitedLines ?? DEFAULT_MAX_LINES,
										maxChars: DEFAULT_MAX_BYTES * 4,
									});
									if (slice.lines.length === 0 && startLine > 0) {
										throw new Error(`Offset ${startLine + 1} is beyond end of file`);
									}
									slicedLines = slice.lines;
									moreContentRemains = !slice.reachedEnd;
								} else {
									const buffer = await ops.readFile(absolutePath);
									const textContent = buffer.toString("utf-8");
									textContentForJsonCheck = textContent;
									const allLines = splitContentLines(textContent);
									totalFileLines = allLines.length;
									// Apply offset/tail if specified. Convert from 1-indexed input to 0-indexed array access.
									if (offset !== undefined) {
										startLine = Math.max(0, offset - 1);
									} else if (tail !== undefined) {
										startLine = Math.max(0, totalFileLines - tail);
										if (limit === undefined) {
											userLimitedLines = tail;
										}
									}
									// Check if offset is out of bounds.
									if (startLine >= allLines.length && allLines.length > 0) {
										throw new Error(
											`Offset ${startLine + 1} is beyond end of file (${allLines.length} lines total)`,
										);
									}
									slicedLines =
										userLimitedLines !== undefined
											? allLines
													.map((line, idx) => ({ text: line, originalIndex: idx + 1 }))
													.slice(startLine, startLine + userLimitedLines)
											: allLines
													.map((line, idx) => ({ text: line, originalIndex: idx + 1 }))
													.slice(startLine);
									moreContentRemains =
										userLimitedLines !== undefined && startLine + userLimitedLines < allLines.length;
								}
								const startLineDisplay = startLine + 1;
								const projectSessionTranscript = isPiSessionJsonlPath(absolutePath, configuredSessionDirectory);
								if (projectSessionTranscript) {
									slicedLines = slicedLines.map((item) => ({
										text: projectPiSessionJsonlLine(item.text),
										originalIndex: item.originalIndex,
									}));
								}
								const firstSelectedLineText = slicedLines[0]?.text ?? "";

								// Safe text filtering
								let canFilter = !projectSessionTranscript;
								if (mimeType) {
									canFilter = false;
								} else {
									const fileNameLower = path.toLowerCase();
									const unsafeExtensions = [
										".json",
										".jsonl",
										".yml",
										".yaml",
										".toml",
										".xml",
										".csv",
										".tsv",
									];
									if (unsafeExtensions.some((ext) => fileNameLower.endsWith(ext))) {
										canFilter = false;
									} else if (textContentForJsonCheck !== undefined) {
										try {
											JSON.parse(textContentForJsonCheck);
											canFilter = false;
										} catch {}
									}
								}

								if (canFilter && filter && filter !== "none") {
									const filtered: typeof slicedLines = [];
									if (filter === "minimal") {
										let consecutiveBlank = 0;
										for (const item of slicedLines) {
											const trimmed = item.text.trimEnd();
											if (trimmed === "") {
												consecutiveBlank++;
												if (consecutiveBlank <= 1) {
													filtered.push({ text: "", originalIndex: item.originalIndex });
												}
											} else {
												consecutiveBlank = 0;
												filtered.push({ text: trimmed, originalIndex: item.originalIndex });
											}
										}
										while (filtered.length > 0 && filtered[filtered.length - 1].text === "") {
											filtered.pop();
										}
									} else if (filter === "aggressive") {
										for (const item of slicedLines) {
											const trimmed = item.text.trimEnd();
											const trimmedStart = trimmed.trimStart();
											if (trimmedStart === "") {
												continue;
											}
											if (
												trimmedStart.startsWith("//") ||
												trimmedStart.startsWith("#") ||
												(trimmedStart.startsWith("/*") && trimmedStart.endsWith("*/"))
											) {
												continue;
											}
											filtered.push({ text: trimmed, originalIndex: item.originalIndex });
										}
									}

									// If filtering would empty a non-empty list, keep original
									const isOriginalNotEmpty = slicedLines.some((item) => item.text.trim().length > 0);
									const isFilteredEmpty = filtered.every((item) => item.text.trim().length === 0);
									if (isOriginalNotEmpty && isFilteredEmpty) {
										// Fallback to slicedLines
									} else {
										slicedLines = filtered;
									}
								}

								const finalLines = slicedLines.map((item) =>
									lineNumbers ? `${item.originalIndex}: ${item.text}` : item.text,
								);
								const selectedContent = finalLines.join("\n");

								// Apply truncation, respecting both line and byte limits.
								const truncation = truncateHead(selectedContent);
								let outputText: string;
								const totalDisplay =
									totalFileLines !== undefined
										? String(totalFileLines)
										: `a ${fileSize !== undefined ? formatSize(fileSize) : "large"} file`;
								if (truncation.firstLineExceedsLimit) {
									// First line alone exceeds the byte limit. Point the model at a bash fallback.
									const firstLineSize = formatSize(Buffer.byteLength(firstSelectedLineText, "utf-8"));
									outputText = `[Line ${startLineDisplay} is ${firstLineSize}, exceeds ${formatSize(DEFAULT_MAX_BYTES)} limit. Use bash: sed -n '${startLineDisplay}p' ${path} | head -c ${DEFAULT_MAX_BYTES}]`;
									details = { truncation };
								} else if (truncation.truncated) {
									// Truncation occurred. Build an actionable continuation notice.
									const endLineDisplay = startLineDisplay + truncation.outputLines - 1;
									const nextOffset = endLineDisplay + 1;
									outputText = truncation.content;
									if (truncation.truncatedBy === "lines") {
										outputText += `\n\n[Showing lines ${startLineDisplay}-${endLineDisplay} of ${totalDisplay}. Use offset=${nextOffset} to continue.]`;
									} else {
										outputText += `\n\n[Showing lines ${startLineDisplay}-${endLineDisplay} of ${totalDisplay} (${formatSize(DEFAULT_MAX_BYTES)} limit). Use offset=${nextOffset} to continue.]`;
									}
									details = { truncation };
								} else if (moreContentRemains) {
									// More content exists beyond this slice; hand the model a continuation pointer.
									const nextOffset = startLine + slicedLines.length + 1;
									const remaining =
										totalFileLines !== undefined && userLimitedLines !== undefined
											? `${totalFileLines - (startLine + userLimitedLines)} more lines in file`
											: "More content remains";
									outputText = `${truncation.content}\n\n[${remaining}. Use offset=${nextOffset} to continue.]`;
								} else {
									// No truncation and no remaining content.
									outputText = truncation.content;
								}
								if (projectSessionTranscript) {
									outputText = `Pi session transcript (user/assistant/summary/tool labels only; thinking and payloads omitted).\n${outputText}`;
								}
								content = [{ type: "text", text: outputText }];
							}

							if (aborted) return;
							signal?.removeEventListener("abort", onAbort);
							resolve({ content, details });
						} catch (error: unknown) {
							signal?.removeEventListener("abort", onAbort);
							if (!aborted) reject(error);
						}
					})();
				},
			);
		},
		renderCall(args, theme, context) {
			const text = (context.lastComponent as Text | undefined) ?? new Text("", 0, 0);
			const classification = !context.expanded ? getCompactReadClassification(args, context.cwd) : undefined;
			text.setText(
				classification
					? formatCompactReadCall(classification, args, theme)
					: formatReadCall(args, theme, context.cwd),
			);
			return text;
		},
		renderResult(result, options, theme, context) {
			const text = (context.lastComponent as Text | undefined) ?? new Text("", 0, 0);
			text.setText(
				formatReadResult(context.args, result, options, theme, context.showImages, context.cwd, context.isError),
			);
			return text;
		},
	};
}

export function createReadTool(cwd: string, options?: ReadToolOptions): AgentTool<typeof readSchema> {
	return wrapToolDefinition(createReadToolDefinition(cwd, options));
}
